import { useState } from 'react'
import { Clock, Calendar } from 'lucide-react'
import { useHomeLogic } from './logic'
import { DemandList } from './components/DemandList'
import { IoAirplaneOutline } from 'react-icons/io5'
import { HiShieldCheck } from 'react-icons/hi'
import { MdToday } from 'react-icons/md'
import { Loading } from '../../../components'
import { motion, type Variants } from 'framer-motion'
import { cn } from '@/lib/utils'

type ViewMode = 'dashboard' | 'kanban'

const fadeInUp: Variants = {
  initial: { opacity: 0, y: -10, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -10, scale: 0.95 },
}

const defaultTransition = { duration: 0.2, ease: 'easeOut' as const }

// Componente wrapper para animações
interface AnimatedColumnProps {
  children: React.ReactNode
  className?: string
}

const AnimatedColumn = ({ children, className }: AnimatedColumnProps) => (
  <motion.div
    variants={fadeInUp}
    initial='initial'
    animate='animate'
    exit='exit'
    transition={defaultTransition}
    className={className}
  >
    {children}
  </motion.div>
)

export const HomeUI = () => {
  const [viewMode] = useState<ViewMode>('dashboard')
  // const navigate = useNavigate()

  const {
    user,
    isLoading,
    getUnassignedDemands,
    getSolvedDemands,
    getAuthorizationDemands,
    getPendingDemands,
    isGestorNoSetorLogado,
  } = useHomeLogic()

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-96'>
        <Loading message='Carregando...' />
      </div>
    )
  }

  if (!user) {
    return (
      <div className='flex items-center justify-center h-96'>
        <div className='text-muted-foreground'>Usuário não encontrado</div>
      </div>
    )
  }

  return (
    <div className='space-y-4 md:space-y-6'>
      {/* Header */}
      <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2'>
        <div>
          <h2 className='text-xl sm:text-2xl md:text-3xl font-bold text-foreground'>
            Bem-vindo, {user.name.split(' ')[0]}!
          </h2>
          <p className='text-sm md:text-base text-muted-foreground mt-1'>
            Aqui está um resumo das suas atividades
          </p>
        </div>
      </div>

      {/* Renderização condicional baseada no viewMode */}
      {viewMode === 'dashboard' ? (
        // Dashboard Grid - 3 colunas fixas x 4 linhas
        <div
          className={cn(
            'grid gap-4 md:gap-6',
            // Mobile: 1 coluna, scroll vertical
            'grid-cols-1',
            // Tablet: 2 colunas
            'sm:grid-cols-2',
            // Desktop: 3 colunas com altura fixa
            'lg:grid-cols-3 lg:grid-rows-4 lg:h-[calc(100vh-200px)]'
          )}
          style={{
            gridTemplateColumns: undefined,
          }}
          //style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)' }}
        >
          {/* Coluna 1: Em atribuição (4 linhas) */}
          <AnimatedColumn className='min-h-[300px] sm:min-h-[350px] lg:row-span-4 lg:col-start-1 lg:min-h-0'>
            <DemandList
              title='Em atribuição'
              demands={getUnassignedDemands()}
              icon={Clock}
              colorClass='bg-blue-500'
              indicatorColor='bg-blue-500'
              cardBackground='bg-blue-50'
              emptyMessage='Fique tranquilo, logo aparecerá uma demanda.'
              emptyIcon={MdToday}
              highlightOverdue={true}
            />
          </AnimatedColumn>

          {/* Coluna 2: Seu dia (4 linhas) */}
          <AnimatedColumn className='min-h-[300px] sm:min-h-[350px] lg:row-span-4 lg:col-start-2 lg:min-h-0'>
            <DemandList
              title='Em andamento'
              demands={getPendingDemands()}
              icon={Calendar}
              colorClass='bg-yellow-500'
              indicatorColor='bg-yellow-500'
              cardBackground='bg-yellow-50'
              emptyMessage='Muito bem, você está fazendo um bom trabalho'
              emptyIcon={MdToday}
              highlightOverdue={true}
            />
          </AnimatedColumn>

          {/* Coluna 3: Autorizações (se GESTOR) + Solucionadas */}
          <div
            className={cn(
              'min-h-[300px] sm:min-h-[350px]',
              'sm:col-span-2 lg:col-span-1',
              'lg:row-span-4 lg:col-start-3 lg:row-start-1 lg:min-h-0',
              isGestorNoSetorLogado && 'flex flex-col gap-4 md:gap-6 h-full'
            )}
          >
            {isGestorNoSetorLogado && (
              <AnimatedColumn className='flex-1 min-h-[250px] lg:min-h-0'>
                <DemandList
                  title='Autorizações'
                  demands={getAuthorizationDemands()}
                  icon={Calendar}
                  colorClass='bg-purple-500'
                  indicatorColor='bg-purple-500'
                  cardBackground='bg-purple-50'
                  emptyMessage='Ainda nada para autorizar. Ótimo sinal!'
                  emptyIcon={HiShieldCheck}
                />
              </AnimatedColumn>
            )}
            <AnimatedColumn className={cn(
              isGestorNoSetorLogado ? 'flex-1 min-h-[250px] lg:min-h-0' : 'h-full min-h-[300px] lg:min-h-0',
            )}>
              <DemandList
                title='Solucionadas'
                demands={getSolvedDemands()}
                icon={Calendar}
                colorClass='bg-green-500'
                indicatorColor='bg-green-500'
                cardBackground='bg-green-50'
                emptyMessage='A semana passa voando. Aproveite o respiro!'
                emptyIcon={IoAirplaneOutline}
              />
            </AnimatedColumn>
          </div>
        </div>
      ) : null}
    </div>
  )
}
